import Add from '@material-ui/icons/Add';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useState, useEffect, Fragment } from 'react';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Mixpanel from '../../shared/Mixpanel';
import CarouselSlideForm from './CarouselSlideForm';
import materialTheme from '../../reactMaterialTheme';
import BrandLogoField from '../ImageUpload/BrandLogoField';
import { useCreativeInput } from '../../hooks';
import './FullAdCarouselBuilder.scss';

const defaultSlide = () => ({
  description: '',
  headline: '',
  mediaUrl: ''
});

const inactiveSlideSelectorClass = slide => {
  if (slide.description && slide.headline && slide.mediaUrl) {
    return 'carousel-slides__selector--filled';
  }

  return '';
};

const MIN_SLIDES_COUNT = 2;
const MAX_SLIDES_COUNT = 10;

const FullAdCarouselBuilder = props => {
  const [creative, setCreative] = useState({
    slides: [defaultSlide(), defaultSlide()],
    slideshowAutoscroll: true,
    ...props.creative
  });
  const creativeInputProps = useCreativeInput(
    ['advertiser', 'name', 'slideshowAutoscroll', 'brandLogoUrl'],
    creative,
    setCreative,
    props.CreativeService
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [behaviors, setBehaviors] = useState(creative.behaviors || {});

  useEffect(() => {
    if (!props.creative.id) {
      Mixpanel.track('New Carousel Demo');
    }
  }, []);

  // need to do this explicitly because we don't updates slides
  // with the creativeInputProps helper
  useEffect(
    function syncSlidesToCreativeService() {
      props.CreativeService.set('slides', creative.slides);
    },
    [creative.slides]
  );

  const addSlide = () => {
    setCreative({ ...creative, slides: [...creative.slides, defaultSlide()] });
  };

  const updateActiveSlide = slideAttributes => {
    const slides = creative.slides.slice();
    slides[activeIndex] = {
      ...creative.slides[activeIndex],
      ...slideAttributes
    };

    setCreative({ ...creative, slides });
  };

  const removeActiveSlide = () => {
    const slides = creative.slides.filter(
      (_slide, index) => index !== activeIndex
    );

    let newActiveIndex =
      activeIndex === creative.slides.length - 1
        ? activeIndex - 1
        : activeIndex;

    setActiveIndex(newActiveIndex);
    setCreative({ ...creative, slides });
  };

  const handleToggleCropBehavior = (event, value) => {
    const newValue = value !== undefined ? value : event.target.value;
    const newBehaviors = { ...behaviors, shouldContainThumbnail: newValue };

    setBehaviors(newBehaviors);
    props.CreativeService.setBehavior('shouldContainThumbnail', newValue);
  };

  return (
    <MuiThemeProvider theme={materialTheme}>
      <div className="carousel-slides">
        <form className="p2">
          <div className="ad-builder">
            <div className="row">
              <TextField
                {...creativeInputProps.nameProps}
                label="Ad Name"
                className="col-xs-10"
                inputProps={{ 'data-testid': 'creative-name-input' }}
                required
              />
            </div>
            <div className="row mt2">
              <TextField
                {...creativeInputProps.advertiserProps}
                label={
                  <label htmlFor={creativeInputProps.advertiserProps.id}>
                    Brand Name
                  </label>
                }
                className="col-xs-10"
                onFocus={event => event.target.select()}
                required
              />
            </div>
            <div className="row mt2">
              <BrandLogoField
                logoUrl={creativeInputProps.brandLogoUrlProps.value}
                onLogoUrlChange={creativeInputProps.brandLogoUrlProps.onChange}
              />
            </div>
            <div className="row mt2">
              <div className="mt4">
                Upload up to 10 slides with images that are 1000x750px or larger
              </div>

              {creative.slides.length > 0 && (
                <Fragment>
                  <div className="carousel-slides__nav">
                    <span className="carousel-slides__label">Slide</span>
                    {creative.slides.map((slide, index) => (
                      <span
                        role="button"
                        tabIndex={0}
                        key={index.toString()}
                        className={`ml2 carousel-slides__selector ${
                          activeIndex === index
                            ? 'carousel-slides__selector--active'
                            : inactiveSlideSelectorClass(slide)
                        }`}
                        onClick={() => setActiveIndex(index)}
                        onKeyDown={() => setActiveIndex(index)}
                        data-testid="slide-nav"
                      >
                        {index + 1}
                      </span>
                    ))}
                    {creative.slides.length < MAX_SLIDES_COUNT && (
                      <span className="carousel-slides__add-button">
                        <Add onClick={addSlide} />
                      </span>
                    )}
                  </div>
                  <CarouselSlideForm
                    removeSlide={
                      creative.slides.length > MIN_SLIDES_COUNT
                        ? removeActiveSlide
                        : null
                    }
                    slide={creative.slides[activeIndex]}
                    updateSlide={updateActiveSlide}
                  />
                  <div className="row">
                    <div className="col-xs-8 col-xs-offset-4 mt2">
                      <FormGroup>
                        <FormControlLabel
                          label="Auto-scroll content"
                          control={
                            <Checkbox
                              inputProps={{
                                'data-testid': 'autoscroll-checkbox'
                              }}
                              name={
                                creativeInputProps.slideshowAutoscrollProps.name
                              }
                              checked={
                                creativeInputProps.slideshowAutoscrollProps
                                  .value
                              }
                              onChange={
                                creativeInputProps.slideshowAutoscrollProps
                                  .onChange
                              }
                            />
                          }
                        />
                        <FormControlLabel
                          label="Do not crop thumbnails"
                          control={
                            <Checkbox
                              checked={behaviors.shouldContainThumbnail}
                              onChange={handleToggleCropBehavior}
                              inputProps={{
                                'data-testid': 'checkbox-behavior-no-crop'
                              }}
                            />
                          }
                        />
                      </FormGroup>
                    </div>
                  </div>
                </Fragment>
              )}
            </div>
          </div>
        </form>
      </div>
    </MuiThemeProvider>
  );
};

FullAdCarouselBuilder.propTypes = {
  creative: PropTypes.object.isRequired,
  CreativeService: PropTypes.shape({
    set: PropTypes.func,
    setDefaultCreative: PropTypes.func,
    setBehavior: PropTypes.func
  }).isRequired,
  $modal: PropTypes.object.isRequired,
  $rootScope: PropTypes.object.isRequired
};

export default FullAdCarouselBuilder;
